import { useCallback, useState, Suspense, useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"
import { UserStylesPanel } from "./UserStylesPanel"
import { useAtomValue } from "jotai"
import { useAtom } from "jotai"
import * as Tabs from "@radix-ui/react-tabs"
import { Tab } from "../ui/Tab"
import { PolarrStylesPanel } from "./PolarrStylesPanel"
import { InternalStylesPanel } from "./InternalStylesPanel"
import { StyleDetailWrapper } from "./StyleDetailWrapper"
import { StylesListType } from "./StylesList"
import { StyleResponseDTO } from "@/api/dto/styles"
import { getCompanyNameShort } from "@/utils/product-name"
import { aiStylesTabAtom } from "@/store/style/style-preferences"
import { StyleTab } from "@/store/style/style-types"
import { showInternalStylesAtom } from "@/store/settings-state"
import { TabButton } from "../ui/TabButton"
import { selectedCategoryAtom, styleCategoriesAtom } from "@/store/style/polarr-styles"
import { userStylesAtom } from "@/store/style/style-state"

// Map backend category names to i18n keys for display
// i18n-ignore-start: backend API category names mapped to i18n keys for display
const categoryNameKeys: Record<string, string> = {
	All: "aiStylesContainer.categoryAll",
	Featured: "aiStylesContainer.categoryFeatured",
	"Analog-Inspired": "aiStylesContainer.categoryAnalogInspired",
	"Natural Color": "aiStylesContainer.categoryNaturalColor",
	"Styled Tones": "aiStylesContainer.categoryStyledTones",
	"Moody & Warm": "aiStylesContainer.categoryMoodyWarm",
}
// i18n-ignore-end
import {
	AI_STYLE_ENTRY_POINT_EXPERIMENT,
	getExperimentVariant,
	getExperimentVariantOverride,
	subscribeExperimentVariantOverride,
} from "@/utils/measurement/experiment"

export interface AIStylesContainerProps {
	title?: string
	onSelect?: (style: StyleResponseDTO | undefined, skipConfirm?: boolean) => void
	onAddNew?: () => void
	showTitle?: boolean
	isFullWidth?: boolean
	onUse?: (style: StyleResponseDTO) => void
	selectedStyle?: StyleResponseDTO | undefined
	onDetailViewChange?: (isDetailView: boolean, style?: StyleResponseDTO, tab?: StyleTab) => void
	selectedDetailStyle?: StyleResponseDTO | null
	selectedDetailSource?: StyleTab
	isLoadingUse?: boolean
}

export const AIStylesContainer = (props: AIStylesContainerProps) => {
	const { t } = useTranslation("components")
	const {
		title,
		showTitle = true,
		onSelect,
		onAddNew,
		isFullWidth,
		onUse,
		onDetailViewChange,
		selectedDetailStyle,
		selectedDetailSource,
		isLoadingUse,
	} = props

	const styleCategories = useAtomValue(styleCategoriesAtom)
	const showInternalStyles = useAtomValue(showInternalStylesAtom)
	const userStyles = useAtomValue(userStylesAtom)
	const [detailViewState, setDetailViewState] = useState<StyleResponseDTO | null>(null)
	const isDetailControlled = selectedDetailStyle !== undefined
	const activeDetailStyle = isDetailControlled ? (selectedDetailStyle ?? null) : detailViewState

	const [selectedStyle, setSelectedStyle] = useState<StyleResponseDTO | undefined>(
		props.selectedStyle,
	)

	const [tab, setTab] = useAtom(aiStylesTabAtom)
	const [aiStyleEntryPointOverride, setAIStyleEntryPointOverride] = useState(() =>
		getExperimentVariantOverride(AI_STYLE_ENTRY_POINT_EXPERIMENT),
	)
	const [selectedCategory, setSelectedCategory] = useAtom(selectedCategoryAtom)
	const onSelectRef = useRef(onSelect)
	const onDetailViewChangeRef = useRef(onDetailViewChange)
	const hasManuallyChangedTabRef = useRef(false)

	useEffect(() => {
		onSelectRef.current = onSelect
	}, [onSelect])

	useEffect(() => {
		onDetailViewChangeRef.current = onDetailViewChange
	}, [onDetailViewChange])

	useEffect(() => {
		return subscribeExperimentVariantOverride(AI_STYLE_ENTRY_POINT_EXPERIMENT, () => {
			setAIStyleEntryPointOverride(
				getExperimentVariantOverride(AI_STYLE_ENTRY_POINT_EXPERIMENT),
			)
		})
	}, [])

	useEffect(() => {
		if (selectedDetailStyle || userStyles.isInitialLoading) {
			return
		}

		let isCancelled = false
		void getExperimentVariant(AI_STYLE_ENTRY_POINT_EXPERIMENT).then((variant) => {
			if (!isCancelled && !hasManuallyChangedTabRef.current) {
				setTab(variant === "treatment" && userStyles.data.length === 0 ? "polarr" : "user")
			}
		})

		return () => {
			isCancelled = true
		}
	}, [
		aiStyleEntryPointOverride,
		selectedDetailStyle,
		setTab,
		userStyles.data.length,
		userStyles.isInitialLoading,
	])

	useEffect(() => {
		onSelectRef.current?.(undefined, false)
	}, [tab])

	useEffect(() => {
		if (!selectedDetailStyle) {
			return
		}
		setSelectedStyle(selectedDetailStyle)
	}, [selectedDetailStyle])

	useEffect(() => {
		if (!selectedDetailSource || selectedDetailSource === tab) {
			return
		}
		setTab(selectedDetailSource)
	}, [selectedDetailSource, tab, setTab])

	useEffect(() => {
		onDetailViewChangeRef.current?.(!!activeDetailStyle, activeDetailStyle ?? undefined, tab)
	}, [activeDetailStyle, tab])

	useEffect(() => {
		if (!isDetailControlled) {
			setDetailViewState(null)
		}
	}, [isDetailControlled])

	const handlePolarrStyleSelected = useCallback(
		(style: StyleResponseDTO | undefined) => {
			setSelectedStyle(style)
			if (style) {
				onSelect?.(style)
				if (isFullWidth && !isDetailControlled) {
					setDetailViewState(style)
				}
			}
		},
		[onSelect, isFullWidth, isDetailControlled],
	)

	const handleUserStyleSelected = useCallback(
		(style: StyleResponseDTO | undefined) => {
			setSelectedStyle(style)
			if (style) {
				onSelect?.(style)
				if (isFullWidth && !isDetailControlled) {
					setDetailViewState(style)
				}
			}
		},
		[onSelect, isFullWidth, isDetailControlled],
	)

	const handleTabChange = (newTab: string) => {
		if (newTab === "user" || newTab === "polarr" || newTab === "internal") {
			hasManuallyChangedTabRef.current = true
			setTab(newTab)
			setSelectedStyle(undefined)
		}
	}

	const handleBackFromDetail = () => {
		if (isDetailControlled) {
			onDetailViewChange?.(false, undefined, tab)
			return
		}
		setDetailViewState(null)
		setSelectedStyle(undefined)
		onSelectRef.current?.(undefined, false)
	}

	const handleViewClick = (style: StyleResponseDTO) => {
		if (isDetailControlled) {
			return
		}
		setDetailViewState(style)
	}

	const handleDetailStyleChange = useCallback(
		(style: StyleResponseDTO) => {
			setSelectedStyle(style)
			if (!isDetailControlled) {
				setDetailViewState(style)
			}
			onDetailViewChangeRef.current?.(true, style, tab)
		},
		[isDetailControlled, tab],
	)

	if (activeDetailStyle) {
		return (
			<Suspense
				fallback={
					<div className="flex h-64 items-center justify-center">
						<div className="text-center">
							<div className="mx-auto mb-2 size-8 animate-spin rounded-full border-2 border-accent-600 border-t-transparent"></div>
							<div className="text-sm text-base-400">
								{t("aiStylesContainer.loadingDetails")}
							</div>
						</div>
					</div>
				}
			>
				<StyleDetailWrapper
					isPolarrStyle={tab === "polarr" || tab === "internal"}
					selectedStyleDetail={activeDetailStyle}
					onBack={handleBackFromDetail}
					onUse={onUse || (() => {})}
					isFullWidth={isFullWidth}
					isLoadingUse={isLoadingUse}
					onStyleChange={handleDetailStyleChange}
				/>
			</Suspense>
		)
	}

	const LAYOUT_CONFIG = {
		containerPadding: isFullWidth ? "px-8 pb-8" : "",
		headerBackground: isFullWidth ? "bg-base-900" : "bg-base-800",
		headerPaddingTop: isFullWidth ? "pt-8" : "",
		polarrCategoryPadding: isFullWidth ? "pb-8" : "pb-4",
		stylesListType: (isFullWidth ? "display" : "select") as StylesListType,
		tabsRootPadding: isFullWidth ? "pb-8" : "pb-4",
	}

	const titleContainer = showTitle && title && (
		<div className="mb-8 shrink-0 text-xl font-bold">{title}</div>
	)

	return (
		<div
			className={`flex size-full flex-col overflow-y-auto ${LAYOUT_CONFIG.containerPadding}`}
		>
			<div
				className={`${LAYOUT_CONFIG.headerBackground} sticky top-0 z-20 ${LAYOUT_CONFIG.headerPaddingTop}`}
			>
				{titleContainer}

				<Tabs.Root
					value={tab}
					className={`${LAYOUT_CONFIG.tabsRootPadding} flex min-h-0 flex-1 flex-col`}
					onValueChange={handleTabChange}
				>
					<Tabs.List
						aria-label={t("aiStylesContainer.ariaAiStylesList")}
						className="flex w-full shrink-0 flex-row space-x-0 overflow-hidden whitespace-nowrap rounded-sm bg-base-700"
					>
						<Tab value="user">{t("aiStylesContainer.myAiStyles")}</Tab>
						<Tab value="polarr">
							{t("aiStylesContainer.brandAiStyles", { brand: getCompanyNameShort() })}
						</Tab>
						{showInternalStyles && (
							<Tab value="internal">{t("aiStylesContainer.internal")}</Tab>
						)}
					</Tabs.List>
				</Tabs.Root>

				{tab === "polarr" && (
					<div
						className={`${LAYOUT_CONFIG.headerBackground} flex items-center gap-6 ${LAYOUT_CONFIG.polarrCategoryPadding}`}
					>
						<div className="no-scrollbar flex min-w-0 flex-1 gap-6 overflow-x-auto">
							{styleCategories.data.map((category) => (
								<TabButton
									role="option"
									ariaSelected={selectedCategory === category.name}
									ariaLabel={t("aiStylesContainer.ariaViewCategory", {
										name: category.name,
									})}
									key={category.categoryId}
									isActive={selectedCategory === category.name}
									onClick={() => setSelectedCategory(category.name)}
									className="shrink-0 whitespace-nowrap"
								>
									{category.name in categoryNameKeys
										? t(categoryNameKeys[category.name] as string)
										: category.name}
								</TabButton>
							))}
						</div>
					</div>
				)}
			</div>

			{tab === "polarr" && (
				<PolarrStylesPanel
					onSelect={handlePolarrStyleSelected}
					selectedStyle={selectedStyle}
					isFullWidth={isFullWidth}
					stylesListType={LAYOUT_CONFIG.stylesListType}
					onViewClicked={!isFullWidth ? handleViewClick : undefined}
				/>
			)}

			{tab === "user" && (
				<UserStylesPanel
					onSelect={handleUserStyleSelected}
					selectedStyle={selectedStyle}
					onAddNew={onAddNew}
					isFullWidth={isFullWidth}
					stylesListType={LAYOUT_CONFIG.stylesListType}
					onViewClicked={!isFullWidth ? handleViewClick : undefined}
				/>
			)}

			{tab === "internal" && showInternalStyles && (
				<InternalStylesPanel
					onSelect={handlePolarrStyleSelected}
					selectedStyle={selectedStyle}
					isFullWidth={isFullWidth}
					stylesListType={LAYOUT_CONFIG.stylesListType}
					onViewClicked={!isFullWidth ? handleViewClick : undefined}
				/>
			)}
		</div>
	)
}
